import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../AuthContext';
import {
    Card,
    CardBody,
    Typography,
} from '@material-tailwind/react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSyncAlt, faCheckCircle, faHistory } from '@fortawesome/free-solid-svg-icons';
import Select from 'react-select';

const { rootUrl } = require('../../../config/config');

const CashbookEntries = ({ accountId }) => {
    const { token } = useAuth();
    const authToken = token || localStorage.getItem('authToken') || 'default-token';

    const [entries, setEntries] = useState([]);
    const [loading, setLoading] = useState(false);
    const [filters, setFilters] = useState({
        entry_type: '',
        date_from: '',
        date_to: '',
        reconciled: ''
    });

    const fetchEntries = useCallback(async () => {
        if (!accountId) return;

        setLoading(true);
        try {
            const params = {
                account_id: accountId,
                ...filters,
                limit: 100
            };

            // Remove empty filters
            Object.keys(params).forEach(key => {
                if (params[key] === '' || params[key] === undefined) {
                    delete params[key];
                }
            });

            const response = await axios.get(
                `${rootUrl}/api/cashbook/entries`,
                {
                    headers: { 'Authorization': `Bearer ${authToken}` },
                    params
                }
            );

            const data = response.data?.success ? response.data.data : (Array.isArray(response.data) ? response.data : []);
            setEntries(data || []);
        } catch (error) {
            console.error('Error fetching entries:', error);
            setEntries([]);
        } finally {
            setLoading(false);
        }
    }, [authToken, accountId, filters]);

    useEffect(() => {
        fetchEntries();
    }, [fetchEntries]);

    const formatDate = (date) => {
        if (!date) return '-';
        return new Date(date).toLocaleDateString('en-US', {
            month: 'short',
            day: 'numeric',
            year: 'numeric'
        });
    };

    const formatCurrency = (amount) => {
        return new Intl.NumberFormat('en-KE', {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
        }).format(Math.abs(amount || 0));
    };

    const getEntryTypeStyles = (type) => {
        const styles = {
            'INCOME': { bg: 'bg-green-100', text: 'text-green-700', label: 'Income' },
            'EXPENSE': { bg: 'bg-red-100', text: 'text-red-700', label: 'Expense' },
            'TRANSFER_IN': { bg: 'bg-blue-100', text: 'text-blue-700', label: 'Transfer In' },
            'TRANSFER_OUT': { bg: 'bg-orange-100', text: 'text-orange-700', label: 'Transfer Out' }
        };
        return styles[type] || { bg: 'bg-gray-100', text: 'text-gray-700', label: type };
    };

    const typeOptions = [
        { value: '', label: 'All Types' },
        { value: 'INCOME', label: 'Income' },
        { value: 'EXPENSE', label: 'Expense' },
        { value: 'TRANSFER_IN', label: 'Transfer In' },
        { value: 'TRANSFER_OUT', label: 'Transfer Out' }
    ];

    const reconciledOptions = [
        { value: '', label: 'All Status' },
        { value: 'true', label: 'Reconciled' },
        { value: 'false', label: 'Not Reconciled' }
    ];

    const selectStyles = {
        control: (base) => ({
            ...base,
            borderRadius: '8px',
            borderColor: '#e5e7eb',
            fontSize: '13px',
            boxShadow: 'none',
            '&:hover': { borderColor: '#FF5733' }
        })
    };

    return (
        <div className="space-y-4 min-w-0">
            <Card className="border border-[#eadfd8] shadow-none rounded-xl bg-white">
                <CardBody className="p-4">
                    <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-3">
                        <div>
                            <Typography className="text-base font-bold text-[#2a2e30]">Entries</Typography>
                            <Typography className="text-sm text-slate-500">Filter and review recent cashbook activity for the selected account.</Typography>
                        </div>
                        <div className="flex items-center gap-2">
                            <button
                                type="button"
                                onClick={() => setFilters({ entry_type: '', date_from: '', date_to: '', reconciled: '' })}
                                className="bg-slate-100 hover:bg-slate-200 text-slate-700 text-sm rounded-sm px-3 py-2 shadow-none"
                            >
                                Reset
                            </button>
                        </div>
                    </div>

                    <div className="mt-4 grid grid-cols-1 xl:grid-cols-[220px_minmax(0,1fr)_220px] gap-4">
                        <div className="min-w-0">
                            <Typography className="text-sm font-semibold text-slate-700 mb-1.5">Type</Typography>
                            <Select
                                options={typeOptions}
                                value={typeOptions.find(o => o.value === filters.entry_type)}
                                onChange={(opt) => setFilters(f => ({ ...f, entry_type: opt.value }))}
                                styles={selectStyles}
                                className="react-select-container"
                                classNamePrefix="react-select"
                                isSearchable={false}
                            />
                        </div>

                        <div className="min-w-0">
                            <Typography className="text-sm font-semibold text-slate-700 mb-1.5">Date Range</Typography>
                            <div className="grid grid-cols-1 sm:grid-cols-[1fr_auto_1fr] gap-2 items-center">
                                <input
                                    type="date"
                                    className="w-full px-3 py-2 bg-white border border-gray-200 rounded-sm text-sm focus:ring-1 focus:ring-[#FF5733] outline-none"
                                    value={filters.date_from}
                                    onChange={(e) => setFilters(f => ({ ...f, date_from: e.target.value }))}
                                />
                                <span className="text-slate-400 text-sm text-center">to</span>
                                <input
                                    type="date"
                                    className="w-full px-3 py-2 bg-white border border-gray-200 rounded-sm text-sm focus:ring-1 focus:ring-[#FF5733] outline-none"
                                    value={filters.date_to}
                                    onChange={(e) => setFilters(f => ({ ...f, date_to: e.target.value }))}
                                />
                            </div>
                        </div>

                        <div className="min-w-0">
                            <Typography className="text-sm font-semibold text-slate-700 mb-1.5">Reconciliation</Typography>
                            <Select
                                options={reconciledOptions}
                                value={reconciledOptions.find(o => o.value === filters.reconciled)}
                                onChange={(opt) => setFilters(f => ({ ...f, reconciled: opt.value }))}
                                styles={selectStyles}
                                className="react-select-container"
                                classNamePrefix="react-select"
                                isSearchable={false}
                            />
                        </div>
                    </div>
                </CardBody>
            </Card>

            {/* Entries Table */}
            {loading ? (
                <div className="flex flex-col items-center justify-center py-20 bg-white rounded-xl border border-[#eadfd8]">
                    <FontAwesomeIcon icon={faSyncAlt} spin className="text-3xl text-[#FF5733] mb-4" />
                    <span className="text-sm font-medium text-gray-500 tracking-wide">Loading transactions...</span>
                </div>
            ) : entries.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-20 bg-white rounded-xl border border-[#eadfd8] border-dashed">
                    <div className="p-4 bg-gray-50 rounded-full mb-4">
                        <FontAwesomeIcon icon={faHistory} className="text-3xl text-gray-300" />
                    </div>
                    <Typography variant="h6" className="text-gray-400 font-bold">
                        No transactions found
                    </Typography>
                    <p className="text-sm text-gray-400">Try adjusting your filters or select another account</p>
                </div>
            ) : (
                <div className="bg-white rounded-xl border border-[#eadfd8] overflow-hidden shadow-none">
                    <div className="overflow-x-auto">
                        <table className="w-full min-w-[900px] text-left border-collapse">
                            <thead>
                                <tr className="bg-slate-50">
                                    <th className="p-3 text-xs font-semibold uppercase tracking-wide text-slate-500 border-b border-slate-200">Date</th>
                                    <th className="p-3 text-xs font-semibold uppercase tracking-wide text-slate-500 border-b border-slate-200">Type</th>
                                    <th className="p-3 text-xs font-semibold uppercase tracking-wide text-slate-500 border-b border-slate-200">Description</th>
                                    <th className="p-3 text-xs font-semibold uppercase tracking-wide text-slate-500 border-b border-slate-200 text-right">Amount</th>
                                    <th className="p-3 text-xs font-semibold uppercase tracking-wide text-slate-500 border-b border-slate-200 text-right">Balance After</th>
                                    <th className="p-3 text-xs font-semibold uppercase tracking-wide text-slate-500 border-b border-slate-200 text-center">Status</th>
                                </tr>
                            </thead>
                            <tbody>
                                {entries.map((entry) => {
                                    const style = getEntryTypeStyles(entry.entry_type);
                                    const isPositive = entry.entry_type === 'INCOME' || entry.entry_type === 'TRANSFER_IN';
                                    
                                    return (
                                        <tr key={entry.id} className="hover:bg-[#fffaf7] transition-colors group">
                                            <td className="p-3 border-b border-slate-100">
                                                <div className="flex flex-col">
                                                    <span className="text-sm font-bold text-gray-700">{formatDate(entry.entry_date)}</span>
                                                    <span className="text-[10px] text-gray-400 font-medium">{new Date(entry.entry_date).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}</span>
                                                </div>
                                            </td>
                                            <td className="p-3 border-b border-slate-100">
                                                <span className={`px-2 py-1 rounded-sm text-[10px] font-bold tracking-tight ${style.bg} ${style.text}`}>
                                                    {style.label}
                                                </span>
                                            </td>
                                            <td className="p-3 border-b border-slate-100">
                                                <div className="max-w-xs xl:max-w-md">
                                                    <Typography className="text-sm font-medium text-gray-600 truncate">
                                                        {entry.description || '-'}
                                                    </Typography>
                                                    {entry.reference_no && (
                                                        <span className="text-[10px] text-gray-400 font-mono">Ref: {entry.reference_no}</span>
                                                    )}
                                                </div>
                                            </td>
                                            <td className="p-3 border-b border-slate-100 text-right">
                                                <span className={`text-sm font-black ${isPositive ? 'text-green-600' : 'text-red-600'}`}>
                                                    {isPositive ? '+' : '-'}{formatCurrency(entry.amount)}
                                                </span>
                                            </td>
                                            <td className="p-3 border-b border-slate-100 text-right">
                                                <span className="text-sm font-bold text-gray-900">
                                                    {formatCurrency(entry.balance_after)}
                                                </span>
                                            </td>
                                            <td className="p-3 border-b border-slate-100 text-center">
                                                {entry.reconciled ? (
                                                    <div className="flex flex-col items-center">
                                                        <FontAwesomeIcon icon={faCheckCircle} className="text-green-500 text-xs" />
                                                        <span className="text-[8px] font-black text-green-600 mt-0.5">VERIFIED</span>
                                                    </div>
                                                ) : (
                                                    <div className="flex flex-col items-center opacity-30 group-hover:opacity-100 transition-opacity">
                                                        <div className="w-1.5 h-1.5 rounded-full bg-gray-300" />
                                                        <span className="text-[8px] font-bold text-gray-400 mt-0.5 uppercase">Pending</span>
                                                    </div>
                                                )}
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}
        </div>
    );
};

export default CashbookEntries;
